import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const languages = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "da", label: "Dansk", flag: "🇩🇰" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
];

export default function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = languages.find((l) => l.code === i18n.language) ?? languages[0];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function selectLanguage(code: string) {
    i18n.changeLanguage(code);
    localStorage.setItem("i18nextLng", code);
    setOpen(false);
  }

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full border border-background-200 bg-background-50 px-3 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t("language")}
      >
        <span className="text-base leading-none">{current.flag}</span>
        <span className="whitespace-nowrap">{current.label}</span>
        <i className="ri-arrow-down-s-line text-sm text-foreground-500"></i>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 z-50 mt-2 w-44 overflow-hidden rounded-lg border border-background-200 bg-white py-1"
        >
          {languages.map((lang) => (
            <li key={lang.code}>
              <button
                type="button"
                onClick={() => selectLanguage(lang.code)}
                className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm transition-colors hover:bg-background-100 ${
                  lang.code === current.code
                    ? "font-semibold text-primary-700"
                    : "text-foreground-700"
                }`}
                role="option"
                aria-selected={lang.code === current.code}
              >
                <span className="text-base leading-none">{lang.flag}</span>
                <span className="whitespace-nowrap">{lang.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}